<template>
    <div v-if="field">
        <b-spinner v-if="loading" />
        <b-alert show v-else-if="noPermissions" variant=""> {{ translator('noPermissionsToInteract') }} </b-alert>
        <template v-else>
            <b-row>
                <b-col :cols="colSize">
                    <b-form-group>
                        <v-select
                            :options="items"
                            @search="debounceFetch"
                            :filterable="false"
                            :required="constraints.required"
                            v-model="content"
                            :reduce="reducer"
                            :getOptionKey="(option) => option && option.value ? option.value : JSON.stringify(option)"
                            @close="onClose"
                            @open="onOpen"
                            :multiple="isOneToMany"
                            :disabled="$attrs.disabled"
                            :selectable="(option) => option ? (isOneToMany ? content.indexOf(option.value) == -1 : true) : false"
                        >
                        <template #list-footer>
                            <li v-show="hasNextPage" ref="load" class="loader" style="text-align:center">
                                <b-spinner small />
                            </li>
                        </template>
                        </v-select>
                    </b-form-group>

                    <b-form-invalid-feedback :id="field.key" style="display:block">
                    {{ errors[0] || null }}
                    </b-form-invalid-feedback>
                </b-col>
                <b-col :cols="12 - colSize">
                    <b-form-group>
                        <slot name="IdentityFieldSelectRelation" v-bind:field="field"></slot>
                    </b-form-group>
                </b-col>
            </b-row>
        </template>
    </div>
</template>

<style scoped lang="scss">
.pagination {
    margin-top: 10px;
}
</style>

<script>
import { buildFiltersFromContext } from '@/components/utils/context'
import vSelect from 'vue-select'
import { isArray, get, template, uniqBy, debounce } from 'lodash'

import "vue-select/src/scss/vue-select.scss";

export default {
    name: "IdentityFieldSelectRelation",
    components: {
        vSelect
    },
    props: {
        field: {
            type: Object
        },
        value: {
            type: String | Object | Array
        },
        store: {
            type: Object
        },
        context: {
            type: Object
        },
        translator: {
            type: Function
        }
    },
    data: function() {
        return {
            items: [],
            models: [],
            errors: [],
            refObj: {},
            renderError: false,
            currentPage: 1,
            limit: 10,
            pages: 0,
            search: '',
            loading: true,
            foreignSettings: null,
            noPermissions: false,
            observer: null
        }
    },
    computed: {
        content: {
            get() {
                if (this.isOneToMany) {
                    if (!this.value) return []
                    let arr = Array.isArray(this.value) ? this.value : []
                    let result = arr.filter(v => v !== null && v !== undefined)
                    return result
                }

                // single-select
                if (!this.value) return this.constraints.default || ""

                let value = this.value
                if (typeof value == 'object' && value !== null) {
                    let key = this.field.relation.key[1]
                    let result = value[key] || ""
                    return result
                }

                return value || ""
            },

            set(val) {
                this.check(val)

                let refObj = null
                if (this.isOneToMany) {
                    let safeVal = Array.isArray(val) ? val : []
                    refObj = this.models.filter(i => {
                        return safeVal.indexOf(i[this.field.relation.key[1]]) != -1
                    })

                    let valObj = Object.assign({}, {
                        _destruct: true,
                        [this.field.key]: safeVal,
                        [`${this.field.key}_RefObj`]: refObj
                    })
                    this.$emit('input', valObj)
                    return
                }

                // single-select
                refObj = this.models.find(i => {
                    return i[this.field.relation.key[1]] == val
                })

                if (val === null || val === '' || val === undefined) {
                    let valObj = Object.assign({}, {
                        _destruct: true,
                        [this.field.key]: null,
                        [`${this.field.key}_RefObj`]: null
                    })
                    this.$emit('input', valObj)
                    return
                }

                if (refObj !== undefined && refObj !== null) {
                    let valObj = Object.assign({}, {
                        _destruct: true,
                        [this.field.key]: val,
                        [`${this.field.key}_RefObj`]: refObj
                    })
                    this.$emit('input', valObj)
                }
            }
        },
        constraints() {
            let constr = this.field.constraints

            return constr
        },

        hasNextPage() {
            return this.currentPage < this.pages
        },

        hasPrevPage() {
            return this.currentPage > 1
        },

        colSize() {
            if (Object.keys(this.$slots).length > 0) {
                return Object.keys(this.$slots).length * 8
            }

            return 12
        },

        isOneToMany() {
            return this.field.relation.type == "one-to-many"
        },

        displayQuickEditorUI() {
            return this.field.relation.foreign_uuid != this.$route.meta.ref
        }
    },
    watch: {
        content(val) {
            this.check(val)
        },

        currentPage() {
            this.fetchPaginate(this.search)
        },
    },
    methods: {
        check(val) {
            this.errors = []

           if (this.constraints.required) {
                if (val !== undefined && val !== null) {
                     if (val == "" || val.length < 1) {
                        this.errors.push(this.translator("identityField.requiredField"))
                    }

                    if (this.isOneToMany) {
                        let found = this.items.filter(i => {
                            return this.content.indexOf(i) == -1
                        })

                        if (found.length < 1) {
                            this.errors.push(this.translator("identityField.invalidSelection"))
                        } 
                    } else {
                        let found = this.items.find(i => {
                            if (typeof i == "object") {
                                return i.value == val
                            } else {
                                return i == val
                            }
                        })
                        
                        if (found === undefined) {
                            this.errors.push(this.translator("identityField.invalidSelection"))
                        } 
                    }
                  
                } else {
                    this.errors.push(this.translator("identityField.requiredField"))
                }
                
            }
            
            this.$emit("error", {field: this.field.key, error: this.errors.length > 0})
        },

        debounceFetch: debounce(function(s) {
            this.fetch(s)
        }, 500),

        fetch(search) {
            this.search = search === undefined ? '' : search;
            this.currentPage = 1;

            let filters = buildFiltersFromContext(this.field.context, this.context)
            let label = this.constraints.relation.label
            //let defaultField = this.foreignField(label)

            if (this.isForeignFieldi18n(label)) {
                let initFilter = `{"${label}.locales.${this.getDefaultLocale(label)}": {"$regex": "${search}", "$options": "i"}}`

                filters.$adv = JSON.parse(initFilter)
            } else {
                filters.filterOn.push(this.constraints.relation.label)
                if (filters.filter.length > 0) {
                    if (this.search != '') {
                        filters.filter += "," + this.search
                    } else {
                        filters.filter += this.search
                    }
                } else {
                    filters.filter += this.search
                }
            }

            if (this.constraints.relation.searchOn) {
                //let c = [];
                
                filters.filterOn = filters.filterOn.concat(this.constraints.relation.searchOn)
                // if fields is i18n then create different filter

            }

            let payload = {
                uuid: this.field.relation.foreign,
                isApp: this.$store.getters.getCurrentContext == "platform-app",
                context: {
                    perPage: this.limit,
                    currentPage: this.currentPage,
                    field: this.field,
                    ...filters
                }
            }
            
            this.store.dispatch("fetchRelationModels", payload).then(resp => {
                let items = resp.items || []
                let label = this.constraints.relation.label
                let value = this.field.relation.key[1]
                this.pages = resp.pages

                let selectedContent = Array.isArray(this.content) ? this.content : (this.content ? [this.content] : [])
                let preservedModels = this.models.filter(m => selectedContent.indexOf(m[value]) !== -1)
                this.models = uniqBy(items.concat(preservedModels), m => m[value])

                this.items = items.map(i => {
                   return this.normalizeLabel(i, label, value)
                }).concat(this.items.filter(i => {
                    return selectedContent.indexOf(i.value) !== -1
                }))

                this.items = uniqBy(this.items, function (e) {
                    return e.value;
                });

            }).catch((err) => {
                this.$debugLog(err)
                this.errors.push(this.translator("identityField.relationshipModelsFetchingFailed"))
            })
        },

        fetchPaginate(search) {
            this.search = search;

            let filters = buildFiltersFromContext(this.field.context, this.context)
            let label = this.constraints.relation.label
            //let defaultField = this.foreignField(label)

            if (this.isForeignFieldi18n(label)) {
                let initFilter = `{"${label}.locales.${this.getDefaultLocale(label)}": {"$regex": "${search}", "$options": "i"}}`

                filters.$adv = JSON.parse(initFilter)
            } else {
                filters.filterOn.push(this.constraints.relation.label)
                if (filters.filter.length > 0) {
                    if (search != '') {
                        filters.filter += "," + search
                    } else {
                        filters.filter += search
                    }
                } else {
                    filters.filter += search
                }
            }            

            if (this.constraints.relation.searchOn) {
                //let c = [];
                
                filters.filterOn = filters.filterOn.concat(this.constraints.relation.searchOn)
                // if fields is i18n then create different filter
            }

            let payload = {
                uuid: this.field.relation.foreign,
                isApp: this.$store.getters.getCurrentContext == "platform-app",
                context: {
                    perPage: this.limit,
                    currentPage: this.currentPage,
                    field: this.field,
                    ...filters
                }
            }
            
            this.store.dispatch("fetchRelationModels", payload).then(resp => {
                let items = resp.items || []
                let label = this.constraints.relation.label
                let value = this.field.relation.key[1]
                this.pages = resp.pages

                let selectedContent = Array.isArray(this.content) ? this.content : (this.content ? [this.content] : [])
                let preservedModels = this.models.filter(m => selectedContent.indexOf(m[value]) !== -1)
                this.models = uniqBy(items.concat(preservedModels), m => m[value])

                this.items = items.map(i => {
                    return this.normalizeLabel(i, label, value)
                }).concat(this.items)

                this.items = uniqBy(this.items, function (e) {
                    return e.value;
                });

            }).catch((err) => {
                this.$debugLog(err)
                this.errors.push(this.translator("identityField.relationshipModelsFetchingFailed"))
            })
        },

        reducer(relation) {
            if (!relation) {
                return "__invalid_value__"
            }

            return relation.value;
        },

        normalizeLabel(item, label, value) {
            let l = null

            if (label.includes("{{") && label.includes("}}")) {
                l = this.parseLabel(item, label)
            }

            if (!l) {
                l = get(item, label, null)
            } 

            if (!l) return {
                label: 'unknown',
                value: item[value]
            }

            if (typeof l == "object") {
                if (l.locales) {
                    return {
                        label: l.locales[this.getDefaultLocale(label)] || l.locales[this.store.getters.getLocale],
                        value: item[value]
                    }
                }
            }

            return {
                label: l,
                value: item[value]
            }
        },

        parseLabel(item, label) {
            try {
                const compiled = template(label, {
                    interpolate: /{{([\s\S]+?)}}/g
                })

                const formated = compiled({
                    item,
                    translateLabel: (label, def) => {
                        return this.$translateLabel(label, def)
                    }
                })
                
                return formated
            } catch {
                return label
            }
        },

        getDefaultLocale(label) {
            let field = this.foreignField(label)

            let { texti18n, i18n } = field.constraints.custom_data
            if (texti18n !== undefined) {
                return texti18n.default_locale
            }

            if (i18n !== undefined) {
                return i18n.default_locale
            }

            return this.store.getters.getLocale
        },

        foreignField(key) {
            if (this.foreignSettings) {
                console.log("foreignSettings", this.foreignSettings)
                return this.foreignSettings.fields.find(f => {
                    return f.key == key
                })
            }

            return null
        },

        isForeignFieldi18n(key) {
            let field = this.foreignField(key)


            if (field) {
                if (field.type) {
                    return field.type.includes('i18n')
                }
            }
            
            return false
        },

        onClose() {
            this.observer.disconnect()
        },

        async infiniteScroll([{ isIntersecting, target }]) {
            console.log("infiniteScroll", isIntersecting, target)

            if (isIntersecting) {
                const ul = target.offsetParent
                const scrollTop = target.offsetParent.scrollTop
                this.currentPage += 1
                this.fetchPaginate(this.search)

                await this.$nextTick()
                ul.scrollTop = scrollTop
            }
        },

        async onOpen() {
            this.fetch(this.search)
            if (this.hasNextPage) {
                await this.$nextTick()
                this.observer.observe(this.$refs.load)
            }
        },
    },

    async created() {
        const { settings } = await this.store.dispatch('fetchComponentsSettings', this.field.relation.foreign)
        this.foreignSettings = settings

        let filters = buildFiltersFromContext(this.field.context, this.context)

        if (this.content.length > 0 && isArray(this.content)) {
            let key = this.field.relation.key[1];

            filters.$adv = {}
            filters.$adv[key] = {
                $in: [...this.content]
            } 
            
        }

        if (!isArray(this.content) && this.content != "") {
            let key = this.field.relation.key[1];

            filters.$adv = {}
            filters.$adv[key] = {
                $in: [this.content]
            }
        }

        let payload = {
            uuid: this.field.relation.foreign,
            isApp: this.store.getters.getCurrentContext == 'platform-app',
            context: {
                perPage: this.limit,
                currentPage: 0,
                field: this.field,
                ...filters
            }
        }


        this.store.dispatch("fetchComponentsModels", payload).then(resp => {
                let items = resp.items || []
                let label = this.constraints.relation.label
                let value = this.field.relation.key[1]
                this.pages = resp.pages

                this.models = items
                this.items = items.map(i => {
                    return this.normalizeLabel(i, label, value)
                })

                this.items = uniqBy(this.items, function (e) {
                    return e.value;
                });

                console.log("Items", payload, this.items)

                this.check(this.content)
            }).catch((err) => {
                if (err.response) {
                    if (err.response.status == 403) {
                        this.noPermissions = true
                    }
                }

                this.$debugLog(err)
                this.errors.push(this.translator("identityField.relationshipModelsFetchingFailed"))
            }).finally(() => {
                this.loading = false
            })
        
    },

    mounted() {
        this.check(this.content)
        this.$emit('input', this.content)
        this.observer = new IntersectionObserver(this.infiniteScroll)
        //this.fetch()
    }
}
</script>